/**
 * Mail operations, expressed as functions rather than raw action names.
 *
 * Everything here goes through `call()` in client.ts. Screens import from this
 * module and never name an action string themselves, so when upstream renames
 * one the change lands in a single file.
 */

import { call, type UploadedAttachment } from './client';
import type {
	FolderCollection,
	Folder,
	Message,
	MessageList
} from './types';

export async function fetchFolders(): Promise<FolderCollection> {
	return call<FolderCollection>('Folders');
}

export interface ListOptions {
	folder: string;
	offset?: number;
	limit?: number;
	search?: string;
}

export async function fetchMessageList(options: ListOptions): Promise<MessageList> {
	return call<MessageList>('MessageList', {
		folder: options.folder,
		offset: options.offset ?? 0,
		limit: options.limit ?? 50,
		search: options.search ?? '',
		sort: '',
		uidNext: 0,
		// Threading is off for now. It changes what a "row" means -- a thread
		// rather than a message -- and that is a decision to take deliberately with
		// the UI to support it, not a flag to leave on by default.
		useThreads: 0
	});
}

export async function fetchMessage(folder: string, uid: number): Promise<Message> {
	return call<Message>('Message', { folder, uid });
}

/** Live unread/total counts for one folder, without re-listing it. */
export async function fetchFolderInformation(
	folder: string
): Promise<{ totalEmails: number; unreadEmails: number; uidNext: number }> {
	return call('FolderInformation', { folder });
}

export async function setSeen(folder: string, uids: number[], seen: boolean): Promise<void> {
	await call<unknown>('MessageSetSeen', {
		folder,
		uids: uids.join(','),
		setAction: seen ? 1 : 0
	});
}

export async function setFlagged(folder: string, uids: number[], flagged: boolean): Promise<void> {
	await call<unknown>('MessageSetFlagged', {
		folder,
		uids: uids.join(','),
		setAction: flagged ? 1 : 0
	});
}

export async function moveMessages(
	fromFolder: string,
	toFolder: string,
	uids: number[]
): Promise<void> {
	await call<unknown>('MessageMove', {
		fromFolder,
		toFolder,
		uids: uids.join(','),
		markAsRead: 0
	});
}

/**
 * Deleting mail means moving it to Trash, except when it is already there.
 *
 * `MessageDelete` is a real IMAP expunge with no undo, so it is reserved for the
 * one case where "delete" genuinely means gone. Everything else is a move, which
 * is what a normal person expects a delete to be.
 */
export async function deleteMessages(
	folder: string,
	uids: number[],
	trashFolder: string | null
): Promise<void> {
	if (trashFolder && trashFolder !== folder) {
		return moveMessages(folder, trashFolder, uids);
	}
	await call<unknown>('MessageDelete', { folder, uids: uids.join(',') });
}

export interface ComposeOptions {
	from: string;
	to: string;
	cc?: string;
	bcc?: string;
	subject: string;
	/** Sanitized outgoing markup. Empty for a plain-text message. */
	html?: string;
	plain: string;
	sentFolder?: string;
	inReplyTo?: string;
	references?: string;
	attachments?: UploadedAttachment[];
	/** Set when re-saving a draft, so the previous copy is replaced not duplicated. */
	draftFolder?: string;
	draftUid?: number;
}

/**
 * The parameter bag shared by SendMessage and SaveMessage.
 *
 * Attachments are keyed by the tempName the upload returned, which is how
 * buildMessage() looks them up in the files provider.
 */
function composePayload(options: ComposeOptions): Record<string, unknown> {
	const attachments: Record<string, unknown> = {};
	for (const file of options.attachments ?? []) {
		attachments[file.tempName] = {
			name: file.name,
			inline: false,
			cId: '',
			location: '',
			type: file.mimeType
		};
	}

	return {
		identityID: '',
		messageFolder: options.draftFolder ?? '',
		messageUid: options.draftUid ?? '',
		from: options.from,
		to: options.to,
		cc: options.cc ?? '',
		bcc: options.bcc ?? '',
		replyTo: '',
		subject: options.subject,
		html: options.html ?? '',
		// Always sent alongside the HTML. A message with no text/plain part is
		// treated as spam by a good number of filters, and is unreadable in any
		// client that prefers plain text.
		plain: options.plain,
		attachments,
		draftInfo: null,
		inReplyTo: options.inReplyTo ?? '',
		references: options.references ?? '',
		requireTLS: 0,
		markAsImportant: 0,
		readReceiptRequest: 0,
		dsn: 0
	};
}

export async function sendMessage(options: ComposeOptions): Promise<void> {
	await call<unknown>('SendMessage', {
		...composePayload(options),
		// Where the sent copy is filed. Empty means the server saves none, which
		// silently loses every sent message from the user's own view.
		saveFolder: options.sentFolder ?? ''
	});
}

/** Saves (or re-saves) a draft. Returns the new folder/uid so the next save replaces it. */
export async function saveDraft(
	options: ComposeOptions,
	draftsFolder: string
): Promise<{ folder: string; uid: number } | null> {
	// Returns {folder, uid} when the server could determine the new UID, and
	// plain `true` when it could not. Re-saving without those would append a
	// second copy instead of replacing the first, so a draft that cannot be
	// located is treated as "no handle" rather than guessed at.
	const result = await call<{ folder?: string; uid?: number } | boolean>('SaveMessage', {
		...composePayload(options),
		saveFolder: draftsFolder
	});

	if (result && 'object' === typeof result && result.folder && result.uid) {
		return { folder: result.folder, uid: Number(result.uid) };
	}
	return null;
}

/**
 * Picks the folder for a role, tolerating servers that advertise none.
 *
 * In order of trust:
 *  1. `preferred` -- the folder the account's own settings name (SentFolder etc.
 *     in app data), when it actually exists. That is what the server itself
 *     falls back to, so the two can never disagree about where Sent is.
 *  2. The IMAP SPECIAL-USE role, when the server sends one.
 *  3. A name match, on either the full path or the last segment -- plenty of
 *     servers (Purelymail included, depending on setup) send no SPECIAL-USE, and
 *     some nest everything under INBOX, so "Sent" is really "INBOX.Sent". Missing
 *     that silently turned off saving sent mail and turned every delete into a
 *     permanent expunge.
 */
export function folderForRole(
	folders: Folder[],
	role: string,
	names: string[] = [],
	preferred?: string
): Folder | null {
	if (preferred) {
		const configured = folders.find(folder => folder.fullName === preferred);
		if (configured) {
			return configured;
		}
	}
	const byRole = folders.find(folder => folder.role === role);
	if (byRole) {
		return byRole;
	}
	const wanted = [role, ...names].map(name => name.toLowerCase());
	return (
		folders.find(folder => wanted.includes(folder.fullName.toLowerCase())) ??
		folders.find(folder => wanted.includes((folder.name ?? '').toLowerCase())) ??
		null
	);
}

/**
 * Orders folders the way a person expects rather than the way IMAP lists them:
 * Inbox first, the other well-known ones next, everything else alphabetical.
 */
const ROLE_ORDER = ['inbox', 'drafts', 'sent', 'archive', 'junk', 'trash'];

export function sortFolders(folders: Folder[]): Folder[] {
	return [...folders].sort((a, b) => {
		const ai = a.role ? ROLE_ORDER.indexOf(a.role) : -1;
		const bi = b.role ? ROLE_ORDER.indexOf(b.role) : -1;
		if (ai !== bi) {
			// Anything without a known role sorts after everything that has one.
			return (ai < 0 ? ROLE_ORDER.length : ai) - (bi < 0 ? ROLE_ORDER.length : bi);
		}
		return a.fullName.localeCompare(b.fullName);
	});
}

/** Folders that cannot hold mail. Listing them just gives people dead ends. */
export function isSelectable(folder: Folder): boolean {
	return !folder.attributes.some(attribute => '\\noselect' === attribute.toLowerCase());
}
